import React, { useRef } from "react"
import { Flex, Box } from "@rebass/grid/emotion"
import gsap from "gsap"
import { useGSAP } from "@gsap/react"
import { ScrollTrigger } from "gsap/ScrollTrigger"

import theme from "@nutrafol/nutrafol-ui-kit/styles/theme"
import { TextElementNova } from "@nutrafol/nutrafol-ui-kit/dist/TextElementNova"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"

import Section from "../section/section"

import {
  Wrapper,
  TimelineItem,
  ProgressBar,
  MobileProgress,
  DesktopProgress,
  Tag,
  CopyWrapper,
} from "./growth.styled"

const MOBILE_HEADER_HEIGHT = 96

const Growth = ({ data }) => {
  const { title, timeline, disclaimer } = data
  const wrapperRef = useRef(null)
  const mobileProgressRef = useRef(null)
  const desktopProgressRef = useRef(null)
  const tagsRef = useRef([])
  const copyRef = useRef([])

  useGSAP(() => {
    let mm = gsap.matchMedia()

    mm.add("(max-width: 767px) and (min-height: 600px)", () => {
      ScrollTrigger.create({
        trigger: wrapperRef.current,
        scrub: 1,
        pin: "#growth-pin-wrapper",
        start: `top-=${MOBILE_HEADER_HEIGHT} top`,
        end: "+=500",
        invalidateOnRefresh: true,
        refreshPriority: 1,
      })

      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.current,
          scrub: 1,
          start: "30% bottom",
          end: "+=1000",
          invalidateOnRefresh: true,
          refreshPriority: 1,
          // markers: true,
        },
      })

      scrollTl
        // Step 1: Progress bar to first label
        .to(
          tagsRef.current[0],
          {
            backgroundColor: theme.wild.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-1"
        )
        .to(
          copyRef.current[0],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-1"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 1,
            slide_title: "Month 1-3",
          })
        )
        // Step 2: Progress to 50%, highlight label and change copy
        .to(mobileProgressRef.current, { height: "calc(50% - 50px)" })
        .to(
          tagsRef.current[1],
          {
            backgroundColor: theme.wild.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-2"
        )
        .to(
          copyRef.current[1],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-2"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 2,
            slide_title: "Month 4-6",
          })
        )
        // Fade in text 3 and progress bar to third label
        .to(mobileProgressRef.current, { height: "calc(100% - 100px)" })
        .to(
          tagsRef.current[2],
          {
            backgroundColor: theme.wild.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-3"
        )
        .to(
          copyRef.current[2],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-3"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 3,
            slide_title: "Month 7+",
          })
        )
        .to(mobileProgressRef.current, { height: "calc(100% - 40px)" })
    })

    mm.add("(min-width: 768px)", () => {
      ScrollTrigger.create({
        trigger: wrapperRef.current,
        scrub: 1,
        pin: "#growth-pin-wrapper",
        start: "center center",
        end: "+=500",
        invalidateOnRefresh: true,
        refreshPriority: 1,
      })

      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.current,
          scrub: 1,
          start: "40% bottom",
          end: "+=1000",
          invalidateOnRefresh: true,
          refreshPriority: 1,
          // markers: true,
        },
      })

      scrollTl
        .to(desktopProgressRef.current, { width: "15%", duration: 0.25 })
        .to(
          tagsRef.current[0],
          {
            backgroundColor: theme.wild.color.nova.brand.accent["80"],
            duration: 0.25,
          },
          "step-1"
        )
        .to(
          copyRef.current[0],
          {
            opacity: 1,
            duration: 0.25,
          },
          "step-1"
        )
        .to(desktopProgressRef.current, { width: "50%", duration: 0.35 })
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 1,
            slide_title: "Month 1-3",
          })
        )
        .to(
          tagsRef.current[1],
          {
            backgroundColor: theme.wild.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-2"
        )
        .to(
          copyRef.current[1],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-2"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 2,
            slide_title: "Month 4-6",
          })
        )
        .to(desktopProgressRef.current, { width: "85%", duration: 0.35 })
        .to(
          tagsRef.current[2],
          {
            backgroundColor: theme.wild.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-3"
        )
        .to(
          copyRef.current[2],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-3"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 3,
            slide_title: "Month 7+",
          })
        )
        .to(desktopProgressRef.current, { width: "100%", duration: 0.15 })
    })
  })

  return (
    <Wrapper ref={wrapperRef} mobileHeaderHeight={MOBILE_HEADER_HEIGHT}>
      <Section className="section-wrapper">
        <Box mb={[32, 32, 40, 80]} className="text-center">
          <TextElementNova
            text={title}
            className="Subtitle-20-Dia-Normal"
            element={`h2`}
          />
        </Box>
        <Flex
          mb={[32, 32, 40, 80]}
          flexDirection={["column", "column", "row"]}
          className="relative sm:gap-10 flex-1"
          justifyContent={["space-between", "space-between", "none"]}
          maxWidth={1120}
          mx="auto"
        >
          <ProgressBar>
            <MobileProgress ref={mobileProgressRef} />
            <DesktopProgress ref={desktopProgressRef} />
          </ProgressBar>
          {timeline.map((item, index) => (
            <TimelineItem
              key={index}
              width={[1, 1, 1 / 3]}
              className={`text-center`}
            >
              <Tag
                className="tag"
                mb={[0, 0, 24]}
                ref={(el) => (tagsRef.current[index] = el)}
              >
                <TextElementNova
                  element={`h3`}
                  text={item.title}
                  className="Caption-10-Dia-Uppercase tag-text"
                />
              </Tag>
              <CopyWrapper>
                <div
                  ref={(el) => (copyRef.current[index] = el)}
                  className="copy-wrapper-ref opacity-60"
                >
                  <TextElementNova
                    text={item.description}
                    className={`copy Body-20-Dia-Normal text-nova-base-black`}
                  />
                </div>
              </CopyWrapper>
            </TimelineItem>
          ))}
        </Flex>
        <Box>
          <TextElementNova
            text={disclaimer}
            className={`copy Caption-10-Dia-Normal text-nova-transparency-black-60 text-center`}
          />
        </Box>
      </Section>
    </Wrapper>
  )
}

export default Growth
